import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonIcon, Icon, Loader } from '@saviynt/design-system';
import cx from 'classnames';

import { UserRequest } from '../../../components';
import { useChatOverlayContext } from '../../../context/chatOverlayContext';
import { useIspmErrorContext } from '../../../context/ispmErrorContext';
import { ErrorTypeEnum } from '../../../data-layer/types';
import {
  CHAT_SKELETON_SCREEN_DELAY_MS,
  CHAT_SKELETON_WIDTHS,
  SCROLL_THRESHOLD,
} from '../../../services/ispmUtil';
import SaviResponse from '../SaviResponse/SaviResponse';

import './SaviChatHistory.css';

export default function SaviChatHistory() {
  const { chatOverlayState } = useChatOverlayContext();
  const { ispmErrorState } = useIspmErrorContext();

  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const [showScrollToBottomButton, setShowScrollToBottomButton] =
    useState(false);

  const reversedMessages = [...chatOverlayState.messages].reverse();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, CHAT_SKELETON_SCREEN_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  // Scroll to the bottom function
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (messagesEndRef.current) {
      const rect = messagesEndRef.current.getBoundingClientRect();
      // isVisible: Controls scroll-to-bottom button visibility based on chat bottom.
      const isDownArrowVisible =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight - SCROLL_THRESHOLD ||
            document.documentElement.clientHeight - SCROLL_THRESHOLD) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth);

      setShowScrollToBottomButton(!isDownArrowVisible); // Show button when bottom is not visible
    }
  };

  // Handle scroll to bottom when button is clicked
  const handleScrollToBottom = () => {
    scrollToBottom();
  };

  return (
    <div
      className={cx({
        'Ispm-SaviChatHistory': true,
        'Ispm-SaviChatHistory--isSplitView':
          chatOverlayState.showGeneratedContent,
        'Ispm-SaviChatHistory--isAlertBannerVisible':
          ispmErrorState.errorType === ErrorTypeEnum.System,
      })}
      onScroll={handleScroll}
      data-testid='chatHistory'>
      {isLoading && (
        <Box className='Ispm-SaviChatHistory-loader'>
          {CHAT_SKELETON_WIDTHS.map((width, index) => (
            // eslint-disable-next-line react/no-array-index-key -- index needed as width is not unique
            <React.Fragment key={`loader-${width}-${index}`}>
              <Loader
                kind='skeleton'
                format='text'
                color='inverse'
                size='large'
                className={`Ispm-SaviChatHistory-loader--${width}`}
              />
            </React.Fragment>
          ))}
        </Box>
      )}

      {!isLoading && (
        <>
          {chatOverlayState.isApiCallInProgress && (
            <div className='Ispm-SaviChatHistory-message'>
              {/*  Without message props - loader will be returned. */}
              <SaviResponse />
            </div>
          )}
          {reversedMessages.map((message, index) => (
            <React.Fragment key={message.id}>
              <div
                className={cx('Ispm-SaviChatHistory-message', {
                  'Ispm-SaviChatHistory-message--isNewSaviResponse':
                    index === 0 && !message.isUser, // Add class to animate the newest message
                })}>
                {/* Timestamp needed in key. */}
                {message.isUser ? (
                  <UserRequest message={message} />
                ) : (
                  <SaviResponse message={message} />
                )}
              </div>

              {message.isUser && message.isDividerRequired && (
                <div className='Ispm-SaviChatHistory-resetDivider'>
                  {/* This is the divider line SVG. Not sure how to move it to design-system as its not an icon */}
                  {/* TODO: Move to design system - https://saviyntars.atlassian.net/browse/IN-12517 */}
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='800'
                    height='2'
                    viewBox='0 0 800 2'
                    fill='none'>
                    <path
                      d='M0 1.25391H800'
                      stroke='#CFD9E9'
                      strokeLinecap='round'
                    />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
          <div
            className='Ispm-SaviChatHistory--scrollToBottomRef'
            ref={messagesEndRef}
          />
        </>
      )}

      {showScrollToBottomButton && (
        <div
          className='Ispm-SaviChatHistory--scrollToBottom'
          data-testid='scrollToBottomButton'>
          <ButtonIcon
            icon={<Icon kind='ArrowDown' size='large' />}
            onClick={handleScrollToBottom}
          />
        </div>
      )}
    </div>
  );
}
